import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useLocation } from 'react-router-dom'

import { useBreadcrumbs } from '../../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { RoutePath } from '../../layout/Routes'
import { CandidateComments } from '../candidateComments/CandidateComments'
import { ContentOrError } from '../shared/somethingWentWrong/SomethingWentWrong'

import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { useQuery, useRequiredParams } from '@percent/partner-dashboard/common/hooks'
import { Flex, Loader, MountableTab, Tab } from '@percent/lemonade'
import { EntryType } from '@percent/partner-dashboard/services/grants/grantsService.types'
import styles from './CandidateDetailsContainer.module.scss'
import { CandidateCard } from './candidateCard/CandidateCard'
import { CandidateDocumentsCard } from './candidateDocumentsCard/CandidateDocumentsCard'
import { CandidateHeader } from './candidateHeader/CandidateHeader'
import { CandidateHeaderTab } from './candidateHeader/CandidateHeader.types'
import { CandidateNominationsCard } from './candidateNominationsCard/CandidateNominationsCard'
import { CandidatePaymentDetails } from './candidatePaymentDetails/CandidatePaymentDetails'
import { CandidateReviewsCard } from './candidateReviewsCard/CandidateReviewsCard'
import { AggregateScores } from './aggregateScores/AggregateScores'
import { CandidateTasks } from './candidateTasks/CandidateTasks'
import { useCandidateComments } from './hooks/useCandidateComments'
import { useCandidateDetails } from './hooks/useCandidateDetails'
import { useCandidateTasks } from './hooks/useCandidateTasks'
import type { CandidateData } from '@percent/utility'
import { SubmittedInfo } from './submittedInfo/SubmittedInfo'
import { CandidateApplicationCard } from './candidateApplicationCard/CandidateApplicationCard'
import { CandidateTimeline } from './candidateTimeline/CandidateTimeline'

export function CandidateDetailsContainer() {
  const [activeTab, setActiveTab] = useState<CandidateHeaderTab>(CandidateHeaderTab.ORGANIZATION_DETAILS)
  const { data, errors, isLoading, refresh } = useCandidateDetails()
  const { grantsService } = useServices()
  const { tasks, isTasksViewOpen, closeTasksView, openTasksView, hasTasks, refreshTasks } = useCandidateTasks()
  const { showComments, handleCloseComments, handleOpenComments, commentsQuery } = useCandidateComments()
  const { state } = useLocation()
  const { t } = useTranslation()
  const { userCan } = usePermissionCheck()
  const canReadGrantDocuments = userCan('readGrantDocuments')
  const canViewGrantPrograms = userCan('viewGrantPrograms')

  const { id: programId } = useRequiredParams()

  const [{ data: program }] = useQuery(grantsService.getProgram, { programId })

  const entryType = program?.entryType

  const isApplicationsView = entryType === EntryType.APPLICATION
  const isNominationsView = entryType === EntryType.NOMINATION || entryType === EntryType.INVITATION

  useBreadcrumbs(
    canViewGrantPrograms
      ? [
          {
            label: t('breadcrumb.grants'),
            path: RoutePath.GRANT_PROGRAMS
          },
          {
            label: state?.organisationName || program?.name || '',
            path: generatePath(RoutePath.GRANT_PROGRAM_CANDIDATES, { id: programId })
          },
          {
            label: data?.candidate?.organisation?.name ?? ''
          }
        ]
      : [
          {
            label: t('breadcrumb.grants.myTasks'),
            path: RoutePath.GRANT_TASKS
          },
          {
            label: data?.candidate?.organisation?.name ?? ''
          }
        ]
  )

  if (isLoading) {
    return <Loader />
  }

  return (
    <Flex gap={16}>
      <Flex data-testid="candidate-details-container" className={styles.detailsCtn}>
        {data.candidate && entryType && (
          <CandidateHeader
            candidate={data.candidate}
            isTasksViewOpen={isTasksViewOpen}
            onOpenTasksView={openTasksView}
            hasTasks={hasTasks}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onShowComments={handleOpenComments}
            commentsShown={showComments}
            totalComments={commentsQuery.totalResults}
            totalNominations={data.nominations?.nominations?.length ?? 0}
            totalDocuments={data.documents?.length ?? 0}
            showDocumentsTab={canReadGrantDocuments}
            formSubmissions={data.formSubmissions}
            entryType={entryType}
          />
        )}
        <Tab value={activeTab} index={CandidateHeaderTab.ORGANIZATION_DETAILS}>
          <ContentOrError error={errors.candidateError}>
            <CandidateCard candidate={data.candidate} />
          </ContentOrError>
        </Tab>
        {isApplicationsView && (
          <Tab value={activeTab} index={CandidateHeaderTab.APPLICATION}>
            <ContentOrError error={errors.candidateError}>
              <CandidateApplicationCard application={data.candidate} />
            </ContentOrError>
          </Tab>
        )}
        {isNominationsView && (
          <Tab value={activeTab} index={CandidateHeaderTab.NOMINATIONS}>
            <ContentOrError error={errors.nominationsError}>
              <CandidateNominationsCard nominations={data.nominations.nominations} />
            </ContentOrError>
          </Tab>
        )}
        <Tab value={activeTab} index={CandidateHeaderTab.SUBMITTED_INFO}>
          <ContentOrError error={errors.formSubmissionsError}>
            <SubmittedInfo organisationSubmissions={data.formSubmissions.filter(s => s.audience === 'organisation')} />
          </ContentOrError>
        </Tab>
        {canReadGrantDocuments && (
          <Tab value={activeTab} index={CandidateHeaderTab.DOCUMENTS}>
            <ContentOrError error={errors.documentsError}>
              <CandidateDocumentsCard
                candidate={data.candidate}
                documents={data.documents}
                onDocsSubmitted={refresh.documents}
              />
            </ContentOrError>
          </Tab>
        )}
        <Tab value={activeTab} index={CandidateHeaderTab.REVIEWS}>
          <ContentOrError error={errors.formSubmissionsError}>
            <AggregateScores scores={data.candidate?.scores ?? []} />
            <CandidateReviewsCard reviews={data.formSubmissions.filter(s => !s.audience || s.audience === 'partner')} />
          </ContentOrError>
        </Tab>
        <Tab value={activeTab} index={CandidateHeaderTab.PAYMENT}>
          <ContentOrError error={errors.candidateError}>
            <CandidatePaymentDetails paymentDetails={data.candidate?.paymentDetails} award={data.candidate?.award} />
          </ContentOrError>
        </Tab>
        {data.candidate && (
          // Mountable, so the unpaginated history is only fetched once the tab is opened.
          <MountableTab value={activeTab} index={CandidateHeaderTab.TIMELINE}>
            <CandidateTimeline programId={programId} candidateId={data.candidate.id} />
          </MountableTab>
        )}
      </Flex>

      {showComments && <CandidateComments commentsQuery={commentsQuery} onClose={handleCloseComments} />}
      {isTasksViewOpen && data.candidate && (
        <CandidateTasks
          tasks={tasks}
          isTaskViewOpen={isTasksViewOpen}
          organisationName={data.candidate.organisation.name}
          candidateData={data.candidate as CandidateData}
          onClose={closeTasksView}
          refreshTasks={refreshTasks}
          refreshFormSubmissions={refresh.formSubmissions}
          refreshDocuments={refresh.documents}
        />
      )}
    </Flex>
  )
}
